import { Trans, useTranslation } from "react-i18next";
import { useState } from "react";
import { Boxes, Loader2, X } from "lucide-react";
import type { ElementBlueprint } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { sideDragProps } from "./side-drag.js";
import { blueprintLabel, mainElementTiles, stepElementCategories, useElementBlueprints } from "./useElementBlueprints.js";

/**
 * The legacy `Hauptelement Auswahl` — pick the step's main element (AR P3).
 *
 * A right-side drawer rather than the legacy's centre dialog (owner), but the same anatomy: a grid of blueprint tiles
 * over a `Beschreibung` panel that fills as you move across them. The panel fills **on hover AND on keyboard focus** —
 * the legacy is hover-only, which makes the descriptions unreachable without a mouse, and the descriptions are the only
 * thing distinguishing several visually similar layouts.
 *
 * This picker REPLACES the step Type dropdown (owner decision). The legacy has no such dropdown because choosing the
 * blueprint *is* the type change — and a blueprint carries more than a type, which the dropdown could not express:
 * takeda runs 3854 steps on `Hauptelement Text links` and 1771 on `MainElement_v2`, both of them `regular`. The server
 * derives the step type from the chosen blueprint.
 */
export function BlueprintPickerDrawer({
  session,
  manualId,
  mode,
  isSensorManual,
  allowSensorCategory = true,
  canUseNonPlaceable = false,
  currentBlueprintId,
  onSelect,
  onClose,
  isSaving = false,
  docked = false,
  target,
}: {
  session: AuthSession | null;
  manualId: number;
  /** `main` = the legacy `Auswählen` (one category, no tabs). `add` = the legacy `+` (category tabs). */
  mode: "main" | "add";
  isSensorManual: boolean;
  /** The caller may see non-placeable templates — superadmin only, from the body capabilities. */
  canUseNonPlaceable?: boolean;
  /** The step's current blueprint — marked as selected, and re-picking it is a server-side no-op. `add` has none. */
  currentBlueprintId?: number | null;
  /** ADR-038: the sensor category's tiles are DEVICES, so a pick names the device as well as the blueprint. */
  onSelect: (blueprintId: number, sensorId?: number) => void;
  /**
   * May the sensor category be offered at all? `false` for the "Alle Schritte" target: a sensor element belongs to a
   * STEP (ADR-038 D10) and the server refuses it there, so every device tile would be a dead end (diff panel).
   */
  allowSensorCategory?: boolean;
  onClose: () => void;
  isSaving?: boolean;
  /** A column beside the editor instead of a modal over it. */
  docked?: boolean;
  /** What the pick goes to, in the title: the page stays usable beside a docked drawer, so it has to say. */
  target?: string;
}) {
  const { t } = useTranslation();
  const { data, isPending, isError } = useElementBlueprints(session, manualId, true);
  const [showNonPlaceable, setShowNonPlaceable] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const categories = stepElementCategories(data, { isSensorManual, showNonPlaceable, allowSensorCategory });
  /** The same set with the placeable filter OFF — what EXISTS, as opposed to what is currently shown. */
  const allCategories = stepElementCategories(data, { isSensorManual, showNonPlaceable: true, allowSensorCategory });
  /**
   * Everything addable here is non-placeable and the switch is off — so point at the switch.
   *
   * Gated on `add` mode AND on the caller actually having the switch (diff-panel R2 consensus): computed from the STEP
   * categories, it was previously true in `main` mode too, hanging "turn on Show non-placeable" over a perfectly good
   * main-element grid that has no such control; and for a normal author it advertised a toggle that never renders.
   */
  const hiddenByFilter = mode === "add" && canUseNonPlaceable && categories.length === 0 && allCategories.length > 0;
  const category = categories.find((c) => c.name === activeCategory) ?? categories[0] ?? null;
  const tiles = mode === "main" ? mainElementTiles(data, isSensorManual) : (category?.blueprints ?? []);
  // The description shown below the grid: whichever tile the pointer or the keyboard is currently on.
  const [active, setActive] = useState<ElementBlueprint | null>(null);

  return (
    <Drawer isOpen isDismissable size="lg" docked={docked} onOpenChange={(open) => !open && onClose()}>
      <DrawerHeader>
        <div className="min-w-0">
          <DrawerTitle className="text-base">{mode === "main" ? t("manualEditor.chooseAMainElement") : t("manualEditor.addAnElement")}{target ? ` · ${target}` : ""}</DrawerTitle>
          <p className="text-muted-foreground text-xs">
            {mode === "main" ? t("manualEditor.theStepsLayoutInArIts") : docked ? t("manualEditor.dragElementsHint") : t("manualEditor.anExtraElementPlacedOnThis")}
          </p>
        </div>
        <Button variant="ghost" size="icon-sm" aria-label={t("manualEditor.close")} onPress={onClose}>
          <X className="size-4" />
        </Button>
      </DrawerHeader>

      {isError ? (
        <p className="text-destructive flex-1 text-sm">{t("manualEditor.catalogueLoadFailed")}</p>
      ) : isPending ? (
        <div className="text-muted-foreground flex flex-1 items-center justify-center gap-2 text-sm">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          {t("manualEditor.loadingElements")}
        </div>
      ) : mode === "add" && (allCategories.length === 0 || (categories.length === 0 && !canUseNonPlaceable)) ? (
        <p className="text-muted-foreground flex-1 text-sm">{t("manualEditor.noAddableElements")}</p>
      ) : tiles.length === 0 && mode === "main" ? (
        // Not a generic "nothing found": a realm with no placeable main elements cannot author AR steps at all, and
        // that is a provisioning fact the author can act on (it is the DatabaseManager's job), not a UI error.
        <p className="text-muted-foreground flex-1 text-sm">{t("manualEditor.noPlaceableMain")}</p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {mode === "add" ? (
            <div className="flex flex-wrap items-center gap-2">
              {/* Round pills, selected = the tenant brand fill (the repo's chip idiom). The legacy's category row. */}
              {categories.map((c) => (
                <Button
                  key={c.name}
                  variant={c.name === category?.name ? "default" : "secondary"}
                  size="xs"
                  className="rounded-full"
                  aria-pressed={c.name === category?.name}
                  onPress={() => setActiveCategory(c.name)}
                >
                  {c.name}
                </Button>
              ))}
              {/* Superadmin only, exactly as the legacy (`ARLMS_BlueprintSelection.js:93-94`). A normal author is not
                  shown a toggle whose result the server would only ever 422. */}
              {canUseNonPlaceable ? (
                <Switch isSelected={showNonPlaceable} onChange={setShowNonPlaceable} className="ml-auto text-xs">
                  {t("manualEditor.showNonPlaceable")}
                </Switch>
              ) : null}
            </div>
          ) : null}
          {hiddenByFilter ? (
            <p className="text-muted-foreground flex-1 text-sm">
              <Trans
                i18nKey="manualEditor.allNonPlaceableFull"
                components={{ name: <span className="text-foreground font-medium">{t("manualEditor.showNonPlaceable")}</span> }}
              />
            </p>
          ) : null}
          <ul className="scroll-thin -mr-2 m-0 grid list-none grid-cols-2 gap-2 overflow-y-auto p-0 pr-2 sm:grid-cols-3">
            {tiles.map((bp) => (
              // Keyed and selected by DEVICE where there is one: a sensor blueprint fans out into one tile per
              // device, so several tiles legitimately share `bp.id` and keying on it alone would collide and
              // highlight every device of that type at once (ADR-038 D1).
              // NAMESPACED: a device tile is keyed by the SENSOR sequence and a template tile by the `ar_element`
              // one. Those are independent, so a bare `sensor?.id ?? id` collapses two id spaces into one and two
              // siblings can collide — React then reuses the wrong tile's state on reorder (diff panel).
              <li key={bp.sensor != null ? `device-${String(bp.sensor.id)}` : `bp-${String(bp.id)}`}>
                <BlueprintTile
                  bp={bp}
                  selected={bp.sensor == null && bp.id === currentBlueprintId}
                  disabled={isSaving}
                  draggable={docked && mode === "add"}
                  onActivate={() => setActive(bp)}
                  onSelect={() => onSelect(bp.id, bp.sensor?.id)}
                />
              </li>
            ))}
          </ul>
          <DescriptionPanel bp={active} />
        </div>
      )}
    </Drawer>
  );
}

/** One tile: the blueprint's real icon (or a glyph), its label, and a selected state. */
function BlueprintTile({
  bp,
  selected,
  disabled,
  draggable,
  onActivate,
  onSelect,
}: {
  bp: ElementBlueprint;
  selected: boolean;
  disabled: boolean;
  draggable: boolean;
  onActivate: () => void;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  // Focus fills the description panel exactly as hover does — the legacy's hover-only behaviour hides every
  // description from a keyboard user, and the description is what separates the near-identical layouts.
  const shared = {
    "aria-pressed": selected,
    onFocus: onActivate,
    onPointerEnter: onActivate,
    onClick: onSelect,
    className: cn(
      "border-border flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-lg border p-2 text-center transition-colors",
      "focus-visible:border-primary focus-visible:ring-primary focus-visible:ring-1 focus-visible:outline-none",
      "disabled:cursor-not-allowed disabled:opacity-50",
      selected ? "border-primary bg-primary/10" : "bg-muted/40 hover:bg-muted",
      !bp.isPlaceable && !selected ? "border-attention border-dashed" : null,
    ),
  };
  const content = (
    <>
        {bp.iconUrl ? (
          <img src={bp.iconUrl} alt="" className="pointer-events-none size-14 rounded object-contain" />
        ) : (
          <Boxes aria-hidden="true" className="text-muted-foreground size-14 p-3" />
        )}
        <span className="line-clamp-2 text-xs">{blueprintLabel(bp)}</span>
        {/* The device's own label, printed on the hardware — the legacy's corner `.idText`, and what an author
            actually looks for when pairing a tile with a box on the rig (ADR-038 §6 #1). */}
        {bp.sensor != null ? <span className="text-muted-foreground font-mono text-[0.625rem] leading-none">{bp.sensor.slug}</span> : null}
        {/* NON-PLACEABLE templates are marked — the legacy outlines them blue (`.BlueprintElement.notPlaceable`,
            Takeda.css:9304) and it is not decoration: takeda ships four whose displayName COLLIDES with a placeable one
            (two tiles both read "Gerader Pfeil"), so without this a superadmin cannot tell which is which. Said in WORDS,
            not just the ring — colour alone is never the only carrier here. */}
        {!bp.isPlaceable ? <span className="text-muted-foreground text-[0.625rem] leading-none">{bp.unavailableReason != null ? t("manualEditor.unavailable") : t("manualEditor.notPlaceable")}</span> : null}
    </>
  );
  // Firefox starts no native drag from a real button, so the draggable tile is a button by role. The icon takes no pointer: a press on an image that failed to load starts no drag in Chrome.
  if (draggable && !disabled) {
    return (
      <div
        role="button"
        tabIndex={0}
        {...shared}
        {...sideDragProps({ kind: "element", blueprintId: bp.id, sensorId: bp.sensor?.id })}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {content}
      </div>
    );
  }
  return (
    <button type="button" disabled={disabled} {...shared}>
      {content}
    </button>
  );
}

/** The `Beschreibung` panel — pinned below the grid so the layout does not jump as descriptions of different lengths load. */
function DescriptionPanel({ bp }: { bp: ElementBlueprint | null }) {
  const { t } = useTranslation();
  return (
    <div className="border-border bg-muted/30 min-h-[5.5rem] shrink-0 rounded-lg border p-3">
      <p className="text-muted-foreground mb-1 text-xs font-medium">{t("manualEditor.sensorDescription")}</p>
      {bp == null ? (
        <p className="text-muted-foreground text-xs">{t("manualEditor.hoverToRead")}</p>
      ) : bp.unavailableReason != null ? (
        // ADR-038 D4: said, not swallowed. The legacy drops such a device with a `console.error` nobody reads, so an
        // operator who registered one of an unsupported type just sees it missing.
        <p className="text-sm">{bp.unavailableReason}</p>
      ) : (
        <p className="text-sm">{bp.description ?? t("manualEditor.noDescriptionForThisElement")}</p>
      )}
    </div>
  );
}
